import { useEffect } from 'react';
import { gtag } from '@/utils/gtag';

interface GoogleAdsEnhancedProps {
  conversionId?: string;
  enableEnhancedConversions?: boolean;
  enablePhoneTracking?: boolean;
  phoneNumber?: string;
}

export const GoogleAdsEnhanced = ({
  conversionId = 'AW-1049756763',
  enableEnhancedConversions = true,
  enablePhoneTracking = true,
  phoneNumber = '+32-2-123-45-67'
}: GoogleAdsEnhancedProps) => {
  useEffect(() => {
    // Configure Google Ads with enhanced features
    gtag('config', conversionId, {
      allow_enhanced_conversions: enableEnhancedConversions,
      phone_conversion_number: enablePhoneTracking ? phoneNumber : undefined,
      send_page_view: true,
      custom_map: {
        'custom_parameter_reservation_type': 'reservation_type',
        'custom_parameter_price': 'price'
      }
    });

    // Track page view specifically for ads
    gtag('event', 'page_view', {
      page_title: document.title,
      page_location: window.location.href,
      send_to: conversionId
    });

    // Enhanced ecommerce setup
    gtag('config', conversionId, {
      currency: 'EUR',
      country: 'BE',
      allow_ad_personalization_signals: true
    });

  }, [conversionId, enableEnhancedConversions, enablePhoneTracking, phoneNumber]);

  return null;
};

// Enhanced conversion tracking functions
export const trackEnhancedReservation = (reservationData: {
  type: string;
  price: number;
  email?: string;
  phone?: string;
  name?: string;
}) => {
  const { type, price, email, phone, name } = reservationData;

  // Enhanced conversion with customer data - VRAI ID DE CONVERSION
  gtag('event', 'conversion', {
    send_to: 'AW-1049756763/W1ONCJSag-kZEIOLqLMq',
    value: price,
    currency: 'EUR',
    transaction_id: 'res_' + Date.now(),
    // Enhanced conversion data
    email: email,
    phone_number: phone,
    first_name: name?.split(' ')[0],
    last_name: name?.split(' ').slice(1).join(' '),
    reservation_type: type
  });

  // Purchase event for remarketing
  gtag('event', 'purchase', {
    transaction_id: 'res_' + Date.now(),
    value: price,
    currency: 'EUR',
    items: [{
      item_id: type.toLowerCase().replace(/\s+/g, '_'),
      item_name: type,
      category: 'Château Gonflable',
      quantity: 1,
      price: price
    }]
  });
};

export const trackEnhancedLead = (leadData: {
  form_type: 'contact' | 'reservation';
  email?: string;
  phone?: string;
  name?: string;
}) => {
  gtag('event', 'conversion', {
    send_to: 'AW-1049756763/W1ONCJSag-kZEIOLqLMq',
    value: leadData.form_type === 'reservation' ? 60 : 0,
    currency: 'EUR',
    // Enhanced conversion data
    email: leadData.email,
    phone_number: leadData.phone,
    first_name: leadData.name?.split(' ')[0],
    last_name: leadData.name?.split(' ').slice(1).join(' ')
  });

  gtag('event', 'generate_lead', {
    currency: 'EUR',
    value: leadData.form_type === 'reservation' ? 60 : 0,
    form_type: leadData.form_type
  });
};

export const trackViewItem = (itemData: {
  item_id: string;
  item_name: string;
  price: number;
  category: string;
}) => {
  gtag('event', 'view_item', {
    currency: 'EUR',
    value: itemData.price,
    items: [itemData]
  });
};

export const trackBeginCheckout = (items: any[], value: number) => {
  gtag('event', 'begin_checkout', {
    currency: 'EUR',
    value: value,
    items: items
  });
};